import { computed, inject, Injectable, signal } from '@angular/core';
import {
  rxResource,
  toObservable,
  takeUntilDestroyed,
} from '@angular/core/rxjs-interop';
import { AuthService } from '@app/core/services/auth/auth.service';
import { ERROR_TOAST_GET_SUBSCRIBERS } from '@shared/constants/toasts/subscriber-http/constants/toast-messages.constants';
import { ToastService } from '@shared/components/toast';
import { StorageKey } from '@shared/enums';
import { StorageService } from '@shared/services/storage.service';
import { of, filter, tap } from 'rxjs';
import { SubscribersHttpService } from '../../features/subscribers/shared/services/subscriber-http.service';
import { getAppId } from '@shared/utils';
import { SubscriberOverview } from '@shared/interfaces/subscribers/subscriber-overview.interface';
import { TenantFacadeService } from '@shared/features/tenant/shared/services/tenant-facade.service';
import { TenantKey } from '@app/core/enums/tenant-key.enum';

interface SubscribersStorage {
  subscribers: SubscriberOverview[];
  selectedSubscriberId: number | null;
}

@Injectable()
export class SubscriberStateService {
  private readonly _storage = inject(StorageService);
  private readonly _toast = inject(ToastService);

  private readonly _authService = inject(AuthService);
  private readonly _appId = getAppId();

  private readonly _subscriberService = inject(SubscribersHttpService);

  private readonly _selectedSubscriberId = signal<number | null>(
    this.subscribersStorage?.selectedSubscriberId,
  );
  private readonly tenantFacade = inject(TenantFacadeService);

  public selectedSubscriberId = this._selectedSubscriberId.asReadonly();

  public setSelectedSubscriberId(subscriberId: number) {
    const currentSubscribersData = this.subscribersStorage;
    this._storage.save(StorageKey.guardianshipSubscribers, {
      ...currentSubscribersData,
      selectedSubscriberId: subscriberId,
    });
    this._selectedSubscriberId.set(subscriberId);
  }

  private readonly _subscribersResource = rxResource({
    params: () => ({
      appId: this._appId(),
      tenant: this.tenantFacade.currentTenantKey(),
    }),
    defaultValue:
      this._storage.read(StorageKey.guardianshipSubscribers)?.subscribers ?? [],
    stream: ({ params }) => {
      const isGuardianshipTenant = params.tenant === TenantKey.GUARDIANSHIP;

      if (params.appId <= 0 || !isGuardianshipTenant) return of([]);

      return this._subscriberService.getSubscribers(Number(params.appId)).pipe(
        tap((subscribers) => {
          const currentSubscribersData = this.subscribersStorage;

          this._storage.save(StorageKey.guardianshipSubscribers, {
            ...currentSubscribersData,
            subscribers,
          });
        }),
      );
    },
  });

  public readonly subscribers = this._subscribersResource.value;

  public readonly isLoading = this._subscribersResource.isLoading;

  public reload() {
    this._subscribersResource.reload();
  }

  public loggedSubscriber = computed(() => {
    return this.subscribers()?.find(
      (subscriber) =>
        subscriber.email === this._authService.sessionUser()?.email,
    );
  });

  constructor() {
    toObservable(this._subscribersResource.error)
      .pipe(
        filter((isError) => !!isError),
        tap(() => {
          const currentSubscribersStorage = this.subscribersStorage;
          this._storage.save(StorageKey.guardianshipSubscribers, {
            ...currentSubscribersStorage,
            subscribers: [],
          });
        }),
        tap(() => {
          this._toast.show(ERROR_TOAST_GET_SUBSCRIBERS);
        }),
        takeUntilDestroyed(),
      )
      .subscribe();
  }

  get subscribersStorage(): SubscribersStorage {
    return (
      this._storage.read(StorageKey.guardianshipSubscribers) ?? {
        subscribers: [],
        selectedSubscriberId: null,
      }
    );
  }
}
